import { useMemo, useState, useCallback } from 'react';
import { computeProductCalculations, applyFestivalDiscountToProductCalc } from './calculations';
import baseURL from '../../../../../../Url/NodeBaseURL';

/**
 * Custom hook for handling all product calculations and product management
 * Separates business logic from UI components
 */
export const useProductCalculations = (productFormData, offers = []) => {
  // State for managing products array
  const [products, setProducts] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  
  // Memoized calculations to prevent unnecessary recalculations
  const calculations = useMemo(() => {
    return computeProductCalculations(productFormData, offers);
  }, [
    productFormData,
    offers,
  ]);

  return calculations;
};

/**
 * Combined hook for product calculations and management
 */
export const useProductForm = (initialProductFormData = {}, offers = [], appliedOffersFromAPI = []) => {
  // Product management state
  const [products, setProducts] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [productFormData, setProductFormData] = useState(initialProductFormData);
  const [rates, setRates] = useState({
    rate_24crt: "",
    rate_22crt: "",
    rate_18crt: "",
    rate_16crt: "",
    silver_rate: "",
  });

  // Helper function to completely clear the form
  const clearFormCompletely = useCallback(() => {
    setProductFormData({
      ...initialProductFormData,
      // Explicitly clear any fields that might be set by external handlers
      code: "",
      product_id: "",
      category: "",
      metal_type: "",
      product_name: "",
      design_name: "",
      purity: "",
      selling_purity: "",
      printing_purity: "",
      sub_category: "",
      gross_weight: "",
      stone_weight: "",
      stone_price: "",
      weight_bw: "",
      va_on: "Gross Weight",
      va_percent: "0",
      wastage_weight: "0.00",
      total_weight_av: "",
      rate: "",
      rate_amt: "0.00",
      mc_on: "MC %",
      mc_per_gram: "",
      making_charges: "",
      hm_charges: "60.00",
      tax_percent: "03% GST",
      tax_amt: "0.00",
      total_price: "0.00",
      qty: "1",
      pieace_cost: "",
      mrp_price: "",
      piece_taxable_amt: "",
      remarks: "",
      product_image: null,
      imagePreview: null,
      custom_purity: "",
      festival_discount: "",
      festival_discount_percentage: "",
      festival_discount_on_rate: "",
      original_total_price: "",
      original_piece_taxable_amt: "",
      transaction_status: "Sales",
      sale_status: "Delivered",
      estimate_status: "Not Delivered",
      opentag_id: "",
      remarks_type: "",
      pricing: "By Weight"
    });
  }, [initialProductFormData]);

  // Get calculations from the original hook
  const calculations = useProductCalculations(productFormData, offers);

  // Use applied offers from API if offers array is empty
  const activeOffers = offers.length > 0 ? offers : appliedOffersFromAPI;

  // Apply festival discount to a product
  const applyFestivalDiscountToProduct = useCallback((product, offer) => {
    return applyFestivalDiscountToProductCalc(product, offer);
  }, []);

  // Validate required fields before adding or updating
  const validateFields = useCallback(async() => {
    const errors = [];

    // Validate Barcode/Rbarcode
    if (!productFormData.code || productFormData.code.trim() === "") {
      errors.push("BarCode/Rbarcode is required");
    }

    // Validate Gross Weight for "By Weight" pricing
    if (productFormData.pricing !== "By fixed" && productFormData.pricing !== "By Running Tag") {
      if (!productFormData.gross_weight || parseFloat(productFormData.gross_weight) <= 0) {
        errors.push("Gross Weight is required and must be greater than 0");
      }
    }
    if(productFormData.pricing=== "By Running Tag"){
      try {
        const response = await fetch(`${baseURL}/get/opening-tags-entry`);
        if (!response.ok) throw new Error("Failed to fetch tag data");

        const result = await response.json();
        const tags = result.result || [];

        const tag = tags.find(
          (t) => String(t.PCode_BarCode) === String(productFormData.code)
        );

        if (tag) {
          if (Number(tag.rem_pcs) < Number(productFormData.qty)) {
            errors.push(
              `Qty cannot exceed available pcs (${tag.rem_pcs} pcs available)`
            );
          }
        }

      } catch (err) {
        console.error("TAG validation failed:", err);
        errors.push("Error validating tag availability");
      }
    }

    if (errors.length > 0) {
      alert(errors.join("\n"));
      return false;
    }

    return true;
  }, [productFormData]);

  // Add new product to the array
  const handleAdd = useCallback(async (onProductAdded) => {
    const isValid = await validateFields();  //Validate fields before adding

    if (!isValid) {
      return null;   
    }

    // Apply all calculations to the product form data
    const productWithCalculations = {
      ...productFormData,
      ...calculations, // Apply all calculated values including festival discount
      id: Date.now(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    // Apply festival discount if offers are available
    let finalProduct = productWithCalculations;
    if (activeOffers && activeOffers.length > 0) {
      const selectedOffer = activeOffers[0];
      finalProduct = applyFestivalDiscountToProduct(productWithCalculations, selectedOffer);
    }
    
    setProducts(prevProducts => [...prevProducts, finalProduct]);
    
    // Clear the form completely to ensure all fields are reset
    clearFormCompletely();
    
    // Call the callback if provided (to reset manual mode and discount)
    if (onProductAdded) {
      onProductAdded();
    }
    
    return finalProduct;
  }, [productFormData, calculations, clearFormCompletely, activeOffers, applyFestivalDiscountToProduct, validateFields]);
  

  // Apply festival discount to all products
  const applyFestivalDiscountToAllProducts = useCallback((offer) => {
    if (!offer) return;
    
    console.log('[applyFestivalDiscountToAllProducts] Applying offer:', offer);
    
    setProducts(prevProducts => {
      const updatedProducts = prevProducts.map(product => {
        const updatedProduct = applyFestivalDiscountToProduct(product, offer);
        console.log('[applyFestivalDiscountToAllProducts] Updated product:', {
          productName: product.product_name,
          oldFestivalDiscount: product.festival_discount,
          newFestivalDiscount: updatedProduct.festival_discount
        });
        return updatedProduct;
      });
      return updatedProducts;
    });
  }, [applyFestivalDiscountToProduct]);

  // Update existing product in the array
  const handleUpdate = useCallback((onProductUpdated) => {
    if (editingIndex === null) return null;
    
    // Validate fields before updating
    if (!validateFields()) {
      return null;
    }
    
    const updatedProduct = {
      ...productFormData,
      updatedAt: new Date().toISOString()
    };
    
    setProducts(prevProducts => {
      const newProducts = [...prevProducts];
      newProducts[editingIndex] = updatedProduct;
      return newProducts;
    });
    
    // Reset editing state
    setIsEditing(false);
    setEditingIndex(null);
    
    // Clear the form completely to ensure all fields are reset
    clearFormCompletely();
    
    // Call the callback if provided (to reset manual mode)
    if (onProductUpdated) {
      onProductUpdated();
    }
    
    return updatedProduct;
  }, [productFormData, editingIndex, clearFormCompletely, validateFields]);

  // Start editing a product
  const startEditing = useCallback((index) => {
    const productToEdit = products[index];
    if (productToEdit) {
      setProductFormData(productToEdit);
      setIsEditing(true);
      setEditingIndex(index);
    }
  }, [products]);

  // Cancel editing
  const cancelEditing = useCallback(() => {
    setIsEditing(false);
    setEditingIndex(null);
    
    // Clear the form completely to ensure all fields are reset
    clearFormCompletely();
  }, [clearFormCompletely]);

  // Delete product from array
  const handleDeleteProduct = useCallback((index) => {
    setProducts(prevProducts => prevProducts.filter((_, i) => i !== index));
  }, []);

  // Get product by index
  const getProductByIndex = useCallback((index) => {
    return products[index] || null;
  }, [products]);

  // Get all products
  const getAllProducts = useCallback(() => {
    return products;
  }, [products]);

  // Clear all products
  const clearAllProducts = useCallback(() => {
    setProducts([]);
  }, []);

  // Load products from external source
  const loadProducts = useCallback((productsData) => {
    setProducts(productsData);
  }, []);

  // Handle form data changes
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setProductFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  // Clear form data
  const clearForm = useCallback(() => {
    clearFormCompletely();
  }, [clearFormCompletely]);

  // Fetch current rates
  const fetchCurrentRates = useCallback(async (baseURL) => {
    try {
      const response = await fetch(`${baseURL}/get/current-rates`);
      if (response.ok) {
        const ratesData = await response.json();
        const newRates = {
          rate_24crt: ratesData.rate_24crt || "",
          rate_22crt: ratesData.rate_22crt || "",
          rate_18crt: ratesData.rate_18crt || "",
          rate_16crt: ratesData.rate_16crt || "",
          silver_rate: ratesData.silver_rate || "",
        };
        setRates(newRates);
        return newRates;
      }
    } catch (error) {
      console.error("Error fetching current rates:", error);
    }
  }, []);

  // Calculate rate based on purity and metal type
  const calculateRateFromPurity = useCallback((purity, metalType, currentRates = rates) => {
    if (!purity || !metalType || !currentRates) return "";

    const metalTypeLower = String(metalType).toLowerCase();
    const purityMatch = purity.match(/(\d+(?:\.\d+)?)/);
    const numericPurity = purityMatch ? parseFloat(purityMatch[1]) : parseFloat(purity);

    if (numericPurity) {
      // For silver, use silver rate directly
      if (metalTypeLower === "silver") {
        return currentRates.silver_rate || "";
      }
      
      // For gold, use specific karat rates based on purity
      if (metalTypeLower === "gold") {
        if (numericPurity === 91.6) {
          return currentRates.rate_22crt || "";
        } else if (numericPurity === 99.9) {
          return currentRates.rate_24crt || "";
        } else if (numericPurity === 75) {
          return currentRates.rate_18crt || "";
        } else {
          // Fallback to 24k rate calculation for other purity values
          const baseRate = currentRates.rate_24crt;
          if (baseRate) {
            return (numericPurity * parseFloat(baseRate)) / 100;
          }
        }
      }
      
      // For other metals, use 24k rate calculation
      const baseRate = currentRates.rate_24crt;
      if (baseRate) {
        return (numericPurity * parseFloat(baseRate)) / 100;
      }
    } else if (metalTypeLower === "silver") {
      return currentRates.silver_rate || "";
    } else {
      return currentRates.rate_24crt || "";
    }

    return "";
  }, [rates]);

  // Auto-calculate rate when purity or metal type changes
  const autoCalculateRate = useCallback(() => {
    if (productFormData.purity && productFormData.metal_type) {
      const rateValue = calculateRateFromPurity(productFormData.purity, productFormData.metal_type, rates);
      setProductFormData(prev => ({
        ...prev,
        rate: rateValue,
      }));
    }
  }, [productFormData.purity, productFormData.metal_type, rates, calculateRateFromPurity]);

  // Auto-select default purity when metal type changes
  const autoSelectDefaultPurity = useCallback((purityOptions) => {
    if (productFormData.metal_type && purityOptions.length > 0 && !productFormData.purity) {
      const defaultPurity = purityOptions.find((option) => {
        const optionValue = option.value || option.label || "";
        return /22/i.test(optionValue) || /24/i.test(optionValue);
      })?.value || purityOptions[0]?.value;
      
      if (defaultPurity) {
        setProductFormData(prev => ({
          ...prev,
          purity: defaultPurity,
        }));
      }
    }
  }, [productFormData.metal_type, productFormData.purity]);

  return {
    // Form state
    productFormData,
    setProductFormData,
    isEditing,
    editingIndex,
    
    // Calculations
    calculations,
    
    // Actions
    handleAdd,
    handleUpdate,
    handleChange,
    clearForm,
    startEditing,
    cancelEditing,
    handleDeleteProduct,
    getProductByIndex,
    getAllProducts,
    clearAllProducts,
    loadProducts,
    
    // Utility functions
    updateFormDataWithCalculations: (data, calc, setter) => 
      updateFormDataWithCalculations(data, calc, setter),
    handleTotalPriceChange: (data, calc, setter, setIsManual) => 
      handleTotalPriceChange(data, calc, setter, setIsManual),
    calculateRateFromPurity,
    
    // Rate and calculation functions
    rates,
    fetchCurrentRates,
    autoCalculateRate,
    autoSelectDefaultPurity,
    applyFestivalDiscountToProduct,
    applyFestivalDiscountToAllProducts,
  };
};

/**
 * Utility function to update form data with calculated values
 */
export const updateFormDataWithCalculations = (productFormData, calculations, setProductFormData) => {
  const updates = {};
  
  // Update basic calculated fields
  if (calculations.weight_bw !== productFormData.weight_bw) updates.weight_bw = calculations.weight_bw;
  if (calculations.wastage_weight !== productFormData.wastage_weight) updates.wastage_weight = calculations.wastage_weight;
  if (calculations.total_weight_av !== productFormData.total_weight_av) updates.total_weight_av = calculations.total_weight_av;
  if (calculations.rate_amt !== productFormData.rate_amt) updates.rate_amt = calculations.rate_amt;
  if (calculations.making_charges !== productFormData.making_charges) updates.making_charges = calculations.making_charges;
  if (calculations.tax_amt !== productFormData.tax_amt) updates.tax_amt = calculations.tax_amt;
  if (calculations.total_price !== productFormData.total_price) updates.total_price = calculations.total_price;
  
  // Update conditional fields
  if (calculations.mc_per_gram && calculations.mc_per_gram !== productFormData.mc_per_gram) {
    updates.mc_per_gram = calculations.mc_per_gram;
  }
  
  if (calculations.piece_taxable_amt && calculations.piece_taxable_amt !== productFormData.piece_taxable_amt) {
    updates.piece_taxable_amt = calculations.piece_taxable_amt;
  }
  
  if (calculations.mrp_price && calculations.mrp_price !== productFormData.mrp_price) {
    updates.mrp_price = calculations.mrp_price;
  }
  
  // Update offer-related fields
  Object.keys(calculations).forEach(key => {
    if (key.startsWith('festival_') || key.startsWith('original_')) {
      if (calculations[key] !== productFormData[key]) {
        updates[key] = calculations[key];
      }
    }
  });
  
  // Apply updates if any
  if (Object.keys(updates).length > 0) {
    setProductFormData(prev => ({ ...prev, ...updates }));
  }
};

/**
 * Utility function to handle total price change and reverse calculate MC per gram
 */
export const handleTotalPriceChange = (productFormData, calculations, setProductFormData, setIsManualTotalPriceChange) => {
  if (calculations.calculateMcPerGramFromTotalPrice !== null) {
    setProductFormData(prev => ({
      ...prev,
      mc_per_gram: calculations.calculateMcPerGramFromTotalPrice
    }));
  }
  setIsManualTotalPriceChange(false);
}; 

 